import { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, InputAdornment, Link as MuiLink, Stack, TextField, Typography } from '@mui/material';
import { ChevronRight, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { normaliseIndianMobile } from '../patient-portal/public-booking/bookingModel';
import { accountChoiceOf, staffMobileSignInModule, type StaffAccountChoice } from '../../lib/api/modules/staffMobileSignIn';
import { enumLabel } from '../../i18n/enumLabel';
import { AuthSubmitButton, alertSx } from './authShared';

/**
 * Staff sign-in with a mobile number: send a code, type it, and (when the number opens several of the person's
 * accounts) pick one. The session response goes to `onSession`, which the login page treats exactly like a password
 * login's — including the two-step verification step.
 */
export function StaffMobileSignIn({ onSession }: { onSession: (response: { data?: unknown; error?: string }) => void }) {
  const { t } = useTranslation('auth');
  const [mobile, setMobile] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [choice, setChoice] = useState<{ selectionToken: string; accounts: StaffAccountChoice[] } | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noAccount, setNoAccount] = useState(false);
  const digits = normaliseIndianMobile(mobile);
  const mobileValid = /^[6-9]\d{9}$/.test(digits);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const run = async (fn: () => Promise<void>) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('mobile.somethingWrong'));
    } finally {
      setLoading(false);
    }
  };

  const send = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!mobileValid || loading) return;
    void run(async () => {
      setNoAccount(false);
      const res = await staffMobileSignInModule.requestCode(digits);
      if (res.error || !res.data?.challengeId) throw new Error(res.error ?? t('mobile.sendFailed'));
      setChallengeId(res.data.challengeId);
      setCode('');
      setResendIn(res.data.resendAfterSeconds ?? 30);
    });
  };

  const verify = (value: string) => {
    if (!challengeId || loading) return;
    void run(async () => {
      const res = await staffMobileSignInModule.verify(challengeId, digits, value);
      if (res.error) {
        if (res.errorCode === 'NO_ACCOUNT') { setNoAccount(true); setChallengeId(null); setCode(''); return; }
        throw new Error(res.error);
      }
      const pick = accountChoiceOf(res);
      if (pick) { setChoice(pick); return; }
      onSession(res);
    });
  };

  const choose = (userId: string) => {
    if (!choice || loading) return;
    void run(async () => {
      const res = await staffMobileSignInModule.choose(choice.selectionToken, userId);
      if (res.error) { setChoice(null); setChallengeId(null); throw new Error(res.error); }
      onSession(res);
    });
  };

  const startOver = () => { setChoice(null); setChallengeId(null); setCode(''); setError(null); };

  if (choice) {
    return (
      <Box data-testid="staff-mobile-choose">
        {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
        <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>{t('mobile.chooseAccount')}</Typography>
        <Stack spacing={1}>
          {choice.accounts.map((a) => (
            <Button key={a.userId} variant="outlined" disabled={loading} onClick={() => choose(a.userId)}
              startIcon={<UserRound size={18} />} endIcon={<ChevronRight size={16} />}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', borderRadius: '12px', py: 1.1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700} noWrap>{a.name || t('mobile.unnamedAccount')}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" noWrap>
                  {[a.role ? enumLabel('role', a.role) : null, a.clinicName].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Button>
          ))}
        </Stack>
        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <MuiLink component="button" type="button" variant="body2" onClick={startOver}>{t('mobile.startOver')}</MuiLink>
        </Box>
      </Box>
    );
  }

  if (!challengeId) {
    return (
      <form onSubmit={send} data-testid="staff-mobile-send">
        {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
        {noAccount && <Alert severity="info" sx={alertSx} data-testid="staff-mobile-no-account">{t('mobile.notTurnedOn')}</Alert>}
        <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>{t('mobile.intro')}</Typography>
        <TextField label={t('mobile.number')} value={mobile} fullWidth autoFocus disabled={loading}
          onChange={(e) => setMobile(e.target.value.replace(/[^\d\s+-]/g, '').slice(0, 16))}
          inputProps={{ inputMode: 'tel', autoComplete: 'tel-national' }}
          InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
          error={digits.length >= 10 && !mobileValid}
          helperText={digits.length >= 10 && !mobileValid ? t('mobile.invalid') : ' '}
          sx={{ mb: 1.5 }} />
        <AuthSubmitButton disabled={!mobileValid || loading} loading={loading} loadingLabel={t('mobile.sending')}>
          {t('mobile.sendCode')}
        </AuthSubmitButton>
      </form>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.length === 6) verify(code); }} data-testid="staff-mobile-code">
      {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
      <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
        {t('mobile.codeSent', { mobile: `${digits.slice(0, 5)} ${digits.slice(5)}` })}{' '}
        <MuiLink component="button" type="button" variant="body2" onClick={startOver}>{t('mobile.changeNumber')}</MuiLink>
      </Typography>
      <TextField label={t('mobile.code')} value={code} fullWidth autoFocus disabled={loading}
        onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setCode(v); if (v.length === 6) verify(v); }}
        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6, style: { letterSpacing: '0.5em', fontSize: 22 } }}
        sx={{ mb: 1.5 }} />
      <AuthSubmitButton disabled={code.length !== 6 || loading} loading={loading} loadingLabel={t('action.signingIn')}>
        {t('action.signIn')}
      </AuthSubmitButton>
      <Box sx={{ mt: 1.5, textAlign: 'center' }}>
        {resendIn > 0 ? (
          <Typography variant="body2" sx={{ color: '#64748b' }}>{t('mobile.resendIn', { s: resendIn })}</Typography>
        ) : (
          <Button size="small" onClick={() => send()} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : undefined}>
            {t('mobile.resend')}
          </Button>
        )}
      </Box>
    </form>
  );
}
